// Role aplikasi — SUMBER TUNGGAL. Tanpa dependensi server (aman di client & edge).
// RBAC v2 (lihat docs/rbac-v2-spec.md). Hierarchy murni 4 level:
//   Staff 0 · Sub-Head 1 · Head 2 · Director 3
// Admin BUKAN role, melainkan flag `is_admin` terpisah di tabel users — urusan
// sistem (kelola user/setting), bukan wewenang bisnis.
// Dua sumbu akses: LEVEL = kedalaman (confidential, kelola, nugasi),
// DEPARTEMEN = keluasan (BU mana saja yang terlihat).
// HOLDING = induk pengawas: anggotanya melihat lintas semua BU HANYA bila Head ke atas.
export const STAFF_ROLE = 'Staff' as const
export const SUBHEAD_ROLE = 'Sub-Head' as const
export const HEAD_ROLE = 'Head' as const
export const DIRECTOR_ROLE = 'Director' as const

export const ROLES = [STAFF_ROLE, SUBHEAD_ROLE, HEAD_ROLE, DIRECTOR_ROLE] as const
export type Role = (typeof ROLES)[number]

/** Jenjang role. Dipakai untuk gate confidential, penugasan, kelola, approval. */
export const ROLE_LEVEL: Record<Role, number> = {
  [STAFF_ROLE]: 0,
  [SUBHEAD_ROLE]: 1,
  [HEAD_ROLE]: 2,
  [DIRECTOR_ROLE]: 3,
}

/**
 * Role pra-v2 yang mungkin masih hidup di JWT lama (masa hidup sesi 8 jam)
 * setelah deploy. DB dimigrasi sekali saat rilis; map ini hanya jaring pengaman
 * runtime dan bisa dihapus setelah semua sesi lama kedaluwarsa.
 */
const LEGACY_ROLE_MAP: Record<string, Role> = {
  Member: STAFF_ROLE,
  HR: STAFF_ROLE,
  Finance: STAFF_ROLE,
  'Managerial Head': SUBHEAD_ROLE,
  'Head BU': HEAD_ROLE,
  'All Role': HEAD_ROLE,
}

/** Normalisasi role: v2 apa adanya, legacy dipetakan, tak dikenal → Staff (fail closed). */
export function normalizeRole(role: string | null | undefined): Role {
  if (role && (ROLES as readonly string[]).includes(role)) return role as Role
  return LEGACY_ROLE_MAP[role ?? ''] ?? STAFF_ROLE
}

/** Level role (legacy dipetakan dulu; tak dikenal → 0 / Staff). */
export function roleLevel(role: string | null | undefined): number {
  return ROLE_LEVEL[normalizeRole(role)]
}

export function isValidRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value)
}

export function isDirector(role: string | null | undefined): boolean {
  return normalizeRole(role) === DIRECTOR_ROLE
}

/**
 * Departemen induk. BUKAN lagi penanda admin — anggota HOLDING mendapat
 * KELUASAN lintas semua BU, dengan kedalaman tetap dibatasi levelnya.
 */
export const SUPER_DEPARTMENT = 'HOLDING' as const

export function isHoldingMember(department: string | null | undefined): boolean {
  return department === SUPER_DEPARTMENT
}

/**
 * Departemen BRANKAS. PROJECT miliknya tidak terlihat lewat jalur keluasan HOLDING —
 * bahkan oleh Head HOLDING. Yang boleh: Director, anggota departemen itu sendiri, atau
 * orang yang terlibat langsung. CERMIN dari server/src/lib/roles.ts.
 */
export const CONFIDENTIAL_DEPARTMENTS = ['HR', 'FINANCE'] as const

export function isConfidentialDepartment(department: string | null | undefined): boolean {
  return !!department && (CONFIDENTIAL_DEPARTMENTS as readonly string[]).includes(department)
}

/**
 * Keluasan pandang LINTAS UNIT. Keanggotaan HOLDING saja TIDAK cukup — butuh level
 * pengawas juga: Director (di mana pun), atau anggota HOLDING ber-role Head ke atas.
 * Staff & Sub-Head selalu fokus pada departemennya sendiri, termasuk yang di HOLDING.
 * HOLDING adalah induk pengawas; tim pelaksana ada di divisi BISNIS & DEVELOPMENT,
 * yang seperti HR dan FINANCE berada di bawah holding tetapi BUKAN unit bisnis.
 */
export function hasCrossDeptView(viewer: {
  role?: string | null
  department?: string | null
}): boolean {
  if (isDirector(viewer.role)) return true
  return isHoldingMember(viewer.department) && roleLevel(viewer.role) >= ROLE_LEVEL[HEAD_ROLE]
}

/** Bentuk minimal viewer untuk semua cek akses (server Viewer & session client). */
export type AccessViewer = {
  role?: string | null
  department?: string | null
  email?: string | null
  username?: string | null
  /** Flag sistem `is_admin` — kelola user/setting, BUKAN akses bisnis. */
  isAdmin?: boolean | null
  /** Grant per user `can_set_visibility` — Staff yang boleh set visibility. */
  canSetVisibility?: boolean | null
}

// ── Sensitivitas data (ide, project, content plan, ticket) ───────────────────
//   'public'   → semua anggota BU (default)
//   'subhead'  → Head ke atas (tingkat lama; batas confidential kini Head)
//   'head'     → Head ke atas
//   'custom'   → hanya orang yang dipilih pembuatnya (+ Head ke atas, lihat ALWAYS_VIEW_LEVEL)
//   'director' → HANYA Director + orang yang dipilih — Head BU pun tidak melihat.
//                Tingkat brankas untuk data sensitif HR/Finance (gaji, dsb).
// Nilai lama 'managerial' & 'confidential' = sinonim 'subhead' (tanpa migrasi data).
export const VISIBILITIES = ['public', 'subhead', 'head', 'custom', 'director'] as const
export type Visibility = (typeof VISIBILITIES)[number]
/** Default untuk item BARU: terbuka untuk seluruh BU. */
export const DEFAULT_VISIBILITY: Visibility = 'public'
/** Nilai legacy di DB → mode v2. */
export const LEGACY_VISIBILITY_MAP: Record<string, Visibility> = {
  managerial: 'subhead',
  confidential: 'subhead',
}

export function normalizeVisibility(value: unknown): Visibility {
  if (typeof value === 'string' && LEGACY_VISIBILITY_MAP[value]) return LEGACY_VISIBILITY_MAP[value]
  return (VISIBILITIES as readonly unknown[]).includes(value) ? (value as Visibility) : DEFAULT_VISIBILITY
}

/** Nilai kolom DB yang berarti mode ini (kanonis + legacy) — untuk klausa SQL. */
export function visibilityDbValues(v: Visibility): string[] {
  const legacy = Object.entries(LEGACY_VISIBILITY_MAP)
    .filter(([, canon]) => canon === v)
    .map(([old]) => old)
  return [v, ...legacy]
}

/**
 * Level minimum pembaca item confidential/sensitif = **Head**.
 * Sub-Head & Staff TIDAK punya akses confidential. Sub-Head memimpin sebuah DIVISI
 * (mis. tech lead di holding), bukan memimpin BU/holding — kedalaman datanya setara
 * staff: hanya 'public'. Yang membaca confidential: Head BU ke atas + Director.
 * CERMIN dari server/src/lib/roles.ts — ubah KEDUA file bersamaan.
 */
export const CONFIDENTIAL_MIN_LEVEL = ROLE_LEVEL[HEAD_ROLE]

/**
 * Level yang SELALU boleh melihat item apa pun di wilayahnya (Head & Director).
 * Pimpinan BU tidak pernah kebutaan data, termasuk atas item 'custom'.
 */
export const ALWAYS_VIEW_LEVEL = ROLE_LEVEL[HEAD_ROLE]

/**
 * Level minimum yang boleh membaca tiap mode lewat jalur "lihat seluruh departemen".
 * GATE CONFIDENTIAL YANG SEBENARNYA. Mode 'subhead' sengaja dinaikkan ke level Head:
 * nilai legacy DB 'managerial' dan 'confidential' dinormalisasi ke mode ini, jadi
 * selama ia di level Sub-Head, setiap baris confidential lama terbaca oleh Sub-Head.
 * CERMIN dari server/src/lib/roles.ts — ubah KEDUA file bersamaan.
 */
const VISIBILITY_MIN_LEVEL: Record<Visibility, number> = {
  public: 0,
  subhead: ROLE_LEVEL[HEAD_ROLE],
  head: ROLE_LEVEL[HEAD_ROLE],
  custom: ALWAYS_VIEW_LEVEL,
  director: ROLE_LEVEL[DIRECTOR_ROLE],
}

/** Mode yang butuh daftar "orang yang dipilih" (chosen viewers) saat submit. */
export function visibilityNeedsViewers(v: Visibility): boolean {
  return v === 'custom' || v === 'director'
}

/** True kalau role-nya boleh membaca item confidential (Head ke atas). */
export function canReadConfidential(role: string | null | undefined): boolean {
  return roleLevel(role) >= CONFIDENTIAL_MIN_LEVEL
}

/**
 * Boleh memilih visibility saat submit?
 * Murni manual: hanya pemegang grant `can_set_visibility`, role apa pun —
 * Director sekalipun tanpa grant tidak bisa. Default grant: HR & Finance.
 * (Parameter role dipertahankan agar call site tidak berubah.)
 */
export function canChooseVisibility(
  _role: string | null | undefined,
  canSetVisibility?: boolean | null,
): boolean {
  return canSetVisibility === true
}

/**
 * Mode visibility yang boleh dibaca role ini lewat jalur "lihat seluruh departemen".
 * Dipakai untuk membangun klausa SQL pada query LIST.
 */
export function readableVisibilities(role: string | null | undefined): Visibility[] {
  const level = roleLevel(role)
  return VISIBILITIES.filter((v) => level >= VISIBILITY_MIN_LEVEL[v])
}

/**
 * Boleh LIHAT sebuah item (ide/ticket/content plan)?
 *  - 'public' → semua yang sudah lolos filter departemen.
 *  - selain itu → pembuat, PIC, dan orang yang dipilih (chosenViewer) selalu boleh;
 *    sisanya harus memenuhi level minimum mode-nya DAN sedepartemen dengan item.
 *  - Director & anggota HOLDING: tanpa batas departemen (keluasan lintas BU).
 */
export function canViewItem(
  viewer: AccessViewer,
  item: {
    department?: string | null
    ownerEmail?: string | null
    assigneeEmail?: string | null
    visibility?: string | null
    /** Viewer termasuk daftar orang pilihan pembuat (mode 'custom'). */
    chosenViewer?: boolean
  },
): boolean {
  const vis = normalizeVisibility(item.visibility)
  if (vis === 'public') return true
  const email = (viewer.email ?? '').toLowerCase()
  if (email && (item.ownerEmail ?? '').toLowerCase() === email) return true
  if (email && (item.assigneeEmail ?? '').toLowerCase() === email) return true
  if (item.chosenViewer) return true
  if (roleLevel(viewer.role) < VISIBILITY_MIN_LEVEL[vis]) return false
  if (isDirector(viewer.role)) return true
  // Keanggotaan HOLDING saja tidak cukup untuk menembus lintas unit — butuh Head ke atas.
  if (hasCrossDeptView(viewer)) return true
  return !!item.department && item.department === viewer.department
}

/**
 * Boleh melihat SELURUH data departemennya (project, task, dsb) — bukan cuma yang
 * melibatkan dia? Sub-Head ke atas. Staff hanya melihat item yang dia pegang/buat
 * (item public ide/ticket tetap terlihat lewat canViewItem).
 */
export function canSeeAllInDepartment(role: string | null | undefined): boolean {
  return roleLevel(role) >= ROLE_LEVEL[SUBHEAD_ROLE]
}

// ── Aturan simetris tunggal: nugasi / kelola / approve ───────────────────────
// Boleh bertindak atas orang (atau item milik orang) yang levelnya DI BAWAH
// level pelaku, dan sedepartemen. Director: siapa pun, lintas BU.
// Diri sendiri selalu boleh (dicek terpisah lewat email/username).

export function canActOn(
  viewer: AccessViewer,
  target: { role?: string | null; department?: string | null },
): boolean {
  const vl = roleLevel(viewer.role)
  if (vl <= roleLevel(target.role)) return false
  if (vl >= ROLE_LEVEL[DIRECTOR_ROLE]) return true
  return !!viewer.department && viewer.department === target.department
}

/** Boleh menugaskan (assign PIC/assignee) ke user target? Diri sendiri selalu boleh. */
export function canAssignTo(
  viewer: AccessViewer,
  target: { role?: string | null; department?: string | null; email?: string | null; username?: string | null },
): boolean {
  const vEmail = (viewer.email ?? '').toLowerCase()
  if (vEmail && (target.email ?? '').toLowerCase() === vEmail) return true
  const vName = (viewer.username ?? '').trim().toLowerCase()
  const tName = (target.username ?? '').trim().toLowerCase()
  if (vName && tName && vName === tName) return true
  return canActOn(viewer, target)
}

/**
 * Boleh edit/hapus sebuah item (project/ide/ticket)?
 *  - Pemilik (pembuat) & PIC → selalu boleh.
 *  - Director → boleh, lintas BU.
 *  - Selain itu: aturan simetris — level di atas level PEMILIK item, sedepartemen.
 *    `ownerRole` wajib di-supply pemanggil server (lookup DB). Bila tidak tersedia
 *    (mis. gating tombol di client), fallback konservatif: Head ke atas sedepartemen.
 */
export function canModifyItem(
  viewer: AccessViewer,
  item: {
    department?: string | null
    ownerEmail?: string | null
    pic?: string | null
    ownerRole?: string | null
  },
): boolean {
  const email = (viewer.email ?? '').toLowerCase()
  const owner = (item.ownerEmail ?? '').toLowerCase()
  if (email && owner && email === owner) return true
  // Pemegang item (PIC) boleh kelola.
  const uname = (viewer.username ?? '').trim().toLowerCase()
  const pic = (item.pic ?? '').trim().toLowerCase()
  if (uname && pic && uname === pic) return true
  if (isDirector(viewer.role)) return true
  const sameDept = !!item.department && item.department === viewer.department
  if (!sameDept) return false
  if (item.ownerRole === undefined || item.ownerRole === null) {
    return roleLevel(viewer.role) >= ROLE_LEVEL[HEAD_ROLE]
  }
  return roleLevel(viewer.role) > roleLevel(item.ownerRole)
}

/**
 * Boleh kelola sebuah PROJECT? Cermin ringkas dari server `getProjectAccess === 'full'`.
 * Aturan project: LIHAT = KELOLA dalam cakupan departemen.
 *  - pemilik / PIC / Director → boleh (lewat canModifyItem)
 *  - anggota HOLDING → boleh, kecuali project milik departemen brankas (HR/FINANCE)
 *  - anggota departemen yang sama, level APA PUN termasuk Staff → boleh; termasuk
 *    departemen tujuan delegasi (assignedDepartment)
 * Daftar project yang sampai ke klien SUDAH difilter server (visibleProject), jadi
 * kedalaman visibility sudah ditegakkan sebelum sampai sini — ini hanya menentukan
 * tampil/tidaknya tombol; server tetap gate ulang setiap aksi.
 * Sengaja TERPISAH dari canModifyItem: helper itu dipakai bersama papan Ide, yang
 * aturannya berbeda (Ide bukan milik semua orang sedepartemen).
 */
/**
 * Boleh HAPUS project? SENGAJA lebih sempit daripada canModifyProject — cermin dari
 * server canDeleteProject. Hapus bersifat permanen dan meruntuhkan task/subtask/catatan,
 * jadi tetap butuh kepemilikan atau wewenang hierarki (pemilik / PIC / Director / atasan
 * pemilik sedepartemen), bukan sekadar berada dalam cakupan pandang. Dipakai juga untuk
 * kontrol kelola kolaborator, yang di server memakai gate sempit yang sama.
 */
export function canDeleteProject(
  viewer: AccessViewer,
  project: {
    department?: string | null
    ownerEmail?: string | null
    pic?: string | null
    ownerRole?: string | null
  },
): boolean {
  return canModifyItem(viewer, project)
}

export function canModifyProject(
  viewer: AccessViewer,
  project: {
    department?: string | null
    assignedDepartment?: string | null
    ownerEmail?: string | null
    pic?: string | null
    ownerRole?: string | null
  },
): boolean {
  if (canModifyItem(viewer, project)) return true
  if (hasCrossDeptView(viewer)) return !isConfidentialDepartment(project.department)
  // Jalur departemen butuh Sub-Head ke atas, selaras dengan server. STAFF sengaja
  // TIDAK mendapatkannya: dia hanya menyentuh project yang benar-benar melibatkan
  // dia (pemilik/PIC/kolaborator), dan untuk itu canModifyItem di atas sudah cukup.
  if (!viewer.department || !canSeeAllInDepartment(viewer.role)) return false
  return project.department === viewer.department || project.assignedDepartment === viewer.department
}

// ── Wewenang kelola user (User Management) — admin only ──────────────────────

/** True kalau viewer memegang flag admin sistem. */
export function isAdminViewer(viewer: AccessViewer): boolean {
  return viewer.isAdmin === true
}

/**
 * Role yang sah untuk sebuah departemen: Director hanya boleh di HOLDING;
 * departemen lain (unit bisnis & tim HR/FINANCE) maksimal Head.
 */
export function rolesForDepartment(department: string | null | undefined): Role[] {
  return department === SUPER_DEPARTMENT ? [...ROLES] : ROLES.filter((r) => r !== DIRECTOR_ROLE)
}

/** Kombinasi role × departemen valid? (normalisasi role legacy dulu) */
export function isRoleAllowedInDepartment(
  role: string | null | undefined,
  department: string | null | undefined,
): boolean {
  return (rolesForDepartment(department) as readonly string[]).includes(normalizeRole(role))
}

/** Role yang boleh di-assign viewer saat invite/edit user. Admin: semua. */
export function assignableRoles(viewer: AccessViewer): Role[] {
  return isAdminViewer(viewer) ? [...ROLES] : []
}

export function canAssignRole(viewer: AccessViewer, role: string): boolean {
  return (assignableRoles(viewer) as readonly string[]).includes(role)
}

/** Boleh kelola (ubah role/departemen/flag, hapus) user target? Admin → siapa pun. */
export function canManageUser(
  viewer: AccessViewer,
  _target?: { role?: string | null; department?: string | null },
): boolean {
  return isAdminViewer(viewer)
}

// Catatan: memberi/mencabut grant `can_set_visibility` HANYA lewat halaman
// admin (PATCH /admin/users) — tidak ada jalur Head/Director non-admin.

// ── Approval project ──────────────────────────────────────────────────────────

/**
 * Boleh meng-ACC/menolak pengajuan milik `owner`? Aturan simetris:
 * atasan pembuat sedepartemen; Director lintas BU.
 */
export function canApproveOf(
  viewer: AccessViewer,
  owner: { role?: string | null; department?: string | null },
): boolean {
  return canActOn(viewer, owner)
}
